import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Grid,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import DatePickerComponent from "../../../../../../../common/components/date-picker/date-picker-compoenent";
import {
  AlertComponent,
  TextInputComponent,
} from "../../../../../../../common/components";
import {
  RESCHEDULE_INITIAL_VALUES,
  RESCHEDULE_VALIDATION_SCHEMA,
} from "./common-dialog-helper";
import { getCurrentUser } from "../../../../../../../common/routines/user-operations";
import dayjs from "dayjs";
import "./common-dialog-task.scss";
import "./common-dialog-call.scss";
import { getUserDetails } from "../../../../../../../api/lead";
import { taskCreate, editTask } from "../../../../../../../api/campaign";
import { useSetLoading } from "../../../../../../../common/hooks/appearance";
import { TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import lockIcon from "../../../../../../../assets/icons/lock-icon.svg";
import { ConfirmationPopup } from "../../../../../../../common/components";
import ConfirmInfo from "../../../../../../../assets/icons/CreateLeadCapturePopupCancelIcon.svg";
import {
  getAssignedPeopleList,
  createCall,
  editCall,
} from "../../../../../../../api/campaign";
import { getCombinedDateTime } from "../../../../../../../utils/utils";
interface CommonCallDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: any) => void;
  title?: string;
  customerDetails?: any;
  initialCall?: any;
  isLogCall?: boolean;
  // setIsConflictWithMeeting?: React.Dispatch<React.SetStateAction<boolean>>;
}
export interface OwnerOption {
  value: string;
  label: string;
  email: string;
  avatar?: string | null;
}
const CommonRescheduleCallDialog: React.FC<CommonCallDialogProps> = ({
  open,
  onClose,
  onSave,
  customerDetails,
  title = "Schedule Call",
  initialCall,
  isLogCall = true,
}) => {
  const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState<boolean>(false);
  const [formInitialValues, setFormInitialValues] = useState(
    RESCHEDULE_INITIAL_VALUES,
  );
  const [customerOptions, setCustomerOptions] = useState<any[]>([]);
  const [isConflictWithMeeting, setIsConflictWithMeeting] = useState(false);
  const [conflictMessage, setConflictMessage] = useState("");

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const fetchCallFor = (call: any) => {
    if (Array.isArray(call?.callFors)) {
      return call.callFors.map((item: any) => item.fullName).join(", ");
    } else {
      return "-";
    }
  };

  const isFutureOrPresent =
    initialCall?.call_start_time &&
    dayjs(initialCall?.call_start_time)?.isAfter(
      dayjs()?.subtract(1, "minute"),
    );

  const computedInitialValues = useMemo(() => {
    if (initialCall) {
      return {
        contact: fetchCallFor(initialCall) || [],
        rescheduleTime: isFutureOrPresent
          ? dayjs(initialCall.call_start_time)
          : dayjs(),
        rescheduleDate: isFutureOrPresent
          ? dayjs(initialCall.call_start_time)
          : dayjs(),
        notes: initialCall?.notes || "",
      };
    }
    return {
      contact: "",
      rescheduleDate: null,
      rescheduleTime: null,
      notes: "",
    };
  }, [initialCall, customerDetails, leadOptions]);

  const [pendingPayload, setPendingPayload] = useState<any>(null);

  const padTime = (val: string | number) => String(val).padStart(2, "0");

  const preparePayload = (values: any) => {
    const payload: any = {
      callFor: initialCall?.callFors?.map((opt: any) => ({
        id: opt?.id,
        resource: opt?.resource,
      })),
      ...(initialCall?.related_to_lead_id
        ? {
            relatedToLeadId: initialCall.related_to_lead_id,
          }
        : { relatedToCustomerId: customerDetails?.id }),
      callType: initialCall?.call_type || null,
      outGoingCallStatus: "Scheduled",
      callStartTime: getCombinedDateTime(
        values.rescheduleDate,
        values.rescheduleTime,
      ),
      ownerId: Number(initialCall.callOwner?.id) || null,
      subject: initialCall.subject || null,
      callPurpose: initialCall.call_purpose || null,
      callAgenda: initialCall?.call_agenda || null,
      notes: values.notes || null,
      cancelNotes: initialCall?.cancel_notes || null,
    };

    if (initialCall?.call_duration) {
      payload.callResult = initialCall.call_result || null;
      payload.description = initialCall.description || null;
      payload.callDuration = initialCall?.call_duration || null;
    }

    return payload;
  };

  const saveCall = async (payload: any, originalValues?: any) => {
    try {
      setLoading(true);
      let response;
      if (initialCall?.id) {
        response = await editCall(initialCall.id, payload);
        setAlertMessage("Call updated successfully.");
      }
      setAlertType("success");
      setAlertOpen(true);
      onSave(response?.data || originalValues);
      onClose();
    } catch (error: any) {
      console.error("Error saving call:", error);
      if (error?.response?.status === 409) {
        setPendingPayload(payload); // Store for re-use
        setIsConflictWithMeeting(true);
        setConflictMessage(
          error?.response?.data?.message ||
            "This call conflicts with an existing meeting or call. Do you want to proceed?",
        );
      } else {
        setAlertMessage(
          error?.response?.data?.message ||
            "Failed to save call. Please try again.",
        );
        setAlertType("error");
        setAlertOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    const payload = preparePayload(values);
    await saveCall(payload, values);
  };

  useEffect(() => {
    const fetchCreatedByOptions = async () => {
      try {
        const response = await getUserDetails({}, "Customer Profile");
        const data = response.data.data.data.data;
        const formattedOptions: OwnerOption[] = data.map((user: any) => ({
          value: String(user.id),
          label: `${user.first_name} ${user.last_name}`,
          email: user.email,
          avatar: user.profile_image_url || null,
        }));

        setLeadOptions(formattedOptions);

        // Set default campaign owner
        const currentUserId = getCurrentUser();
        const matchedUser = formattedOptions?.find(
          (opt) => opt.value === String(currentUserId?.id),
        );
        if (matchedUser) {
          setFormInitialValues((prev: any) => ({
            ...prev,
            callOwner: matchedUser.value,
            relatedTo: customerDetails?.name || "",
          }));
        }
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
            "Something went wrong please try again.",
        );
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    };

    fetchCreatedByOptions();
  }, [customerDetails]);

  const fetchAssignedPeople = async () => {
    try {
      const response = await getAssignedPeopleList(customerDetails?.id);
      if (response.status === 200 && response.data.success) {
        const formattedOptions = response?.data?.data?.map((item: any) => ({
          value: item.id || null,
          label: item?.fullName || "",
          email: item?.primaryEmail || "",
          avatar: item?.primary_email || null,
          resource: item?.resource || null,
        }));
        setCustomerOptions(formattedOptions);
      }
    } catch (error: any) {
      console.error("Error while fetching customers:", error);
      setAlertMessage(
        error?.response?.data?.message ||
          "Failed to fetch customers. Please try again.",
      );
      setAlertType("error");
      setAlertOpen(true);
      return [];
    }
  };

  useEffect(() => {
    if (open) {
      fetchAssignedPeople();
    }
  }, [open, customerDetails]);

  const roundToNext15Min = (time: dayjs.Dayjs) => {
    const minutes = time.minute();
    const remainder = 15 - (minutes % 15);
    return time.add(remainder, "minute").startOf("minute");
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              minWidth: "650px",
              // minHeight: "108px",
              position: "absolute",
              top: 0,
              margin: 0,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 1,
            paddingLeft: 3,
            borderBottom: "1px solid #E1E1E1",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold !important" }} // You can also use 700 or 'bold'
          >
            {title}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik
          enableReinitialize
          initialValues={computedInitialValues}
          // validationSchema={withContext(CALL_DIALOG_VALIDATION_SCHEMA(), { isLogCall })}
          validationSchema={RESCHEDULE_VALIDATION_SCHEMA}
          onSubmit={(values) => handleSave(values)}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
            dirty,
          }) => {
            return (
              <Form>
                <div className="task-dialog-container task-dialog-call-container">
                  <DialogContent>
                    <Grid container spacing={2}>
                      {/* Contact */}
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={6}
                          className=""
                        >
                          <Typography width={200}>Contact</Typography>
                          <Box flex={1} className="static-field">
                            <TextInputComponent
                              type="text"
                              fieldName="contact"
                              optional={false}
                              disabled={true}
                              value={values.contact}
                              maxlength="200"
                            />
                            <Box className="lock-icon-wrapper">
                              <img
                                src={lockIcon}
                                alt="lock icon"
                                style={{ width: 20, height: 20 }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Reschedule Start Time */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={6}>
                          <Typography width={200}>Reschedule Date</Typography>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box flex={1} className="call-start-time-wrapper">
                              <Box className="call-start-time-picker">
                                <DatePickerComponent
                                  label="date"
                                  fieldName="rescheduleDate"
                                  value={values?.rescheduleDate || null}
                                  onChange={(date) =>
                                    setFieldValue("rescheduleDate", date)
                                  }
                                  minDate={dayjs()}
                                  optional={true}
                                  isLabel={false}
                                />
                              </Box>
                              <Box className="call-start-time-picker">
                                <TimePicker
                                  label="Select Time"
                                  value={values.rescheduleTime || null}
                                  onChange={(newTime) =>
                                    setFieldValue("rescheduleTime", newTime)
                                  }
                                  minTime={
                                    dayjs(values.rescheduleTime).isSame(
                                      dayjs(),
                                      "day",
                                    )
                                      ? roundToNext15Min(dayjs())
                                      : dayjs().startOf("day")
                                  }
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      error: Boolean(
                                        touched.rescheduleTime &&
                                          errors.rescheduleTime,
                                      ),
                                      helperText:
                                        touched.rescheduleTime &&
                                        errors.rescheduleTime
                                          ? typeof errors.rescheduleTime ===
                                            "string"
                                            ? errors.rescheduleTime
                                            : Array.isArray(
                                                  errors.rescheduleTime,
                                                )
                                              ? (
                                                  errors.rescheduleTime as string[]
                                                ).join(", ")
                                              : " "
                                          : " ",
                                      InputProps: {
                                        disableUnderline: true,
                                        style: {
                                          border: "none",
                                        },
                                      },
                                      sx: {
                                        ".MuiOutlinedInput-notchedOutline": {
                                          border: "none", // remove border
                                        },
                                        ".MuiInputBase-input": {
                                          padding: 0,
                                        },
                                        // Hide label *only when floating/shrink*
                                        "& label.MuiInputLabel-shrink": {
                                          display: "none",
                                        },
                                      },
                                    },
                                  }}
                                />
                              </Box>
                            </Box>
                          </LocalizationProvider>
                        </Box>
                      </Grid>

                      {/* Notes */}
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="notes" gap={6}>
                          <Typography width={200}>Notes</Typography>
                          <Box flex={1}>
                            <TextInputComponent
                              type="text"
                              fieldName="notes"
                              placeholder="Add subject"
                              optional={false}
                              disabled={false}
                              value={values.notes}
                              onChange={handleChange}
                              maxlength="200"
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions sx={{ margin: "12px" }}>
                    <button
                      type="button"
                      onClick={onClose}
                      className="cancel-button"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="cancel-button next-button-wrapper"
                      disabled={!dirty || loading}
                    >
                      Save
                    </button>
                  </DialogActions>
                </div>
              </Form>
            );
          }}
        </Formik>
        <div className="mark-completed">
          <ConfirmationPopup
            open={isConflictWithMeeting}
            onCancel={() => {
              setIsConflictWithMeeting(false);
              setConflictMessage("");
            }}
            onOkay={() => {
              if (pendingPayload) {
                const updatedPayload = {
                  ...pendingPayload,
                  ignoreOverlap: true, // ✅ Add this key when retrying
                };
                saveCall(updatedPayload);
                setIsConflictWithMeeting(false);
                setConflictMessage("");
                setPendingPayload(null);
              }
            }}
            title={conflictMessage}
            note="Are you sure you want to create this call anyway?"
            buttonText="Confirm"
            Icon={ConfirmInfo}
            className="mark-completed-confirmation"
          />
        </div>
      </Dialog>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={handleAlertClose}
      />
    </>
  );
};

export default CommonRescheduleCallDialog;
